import { useCallback, useEffect, useState } from 'react';
import { setCallsign, supabase, useAccount } from '../game/account';
import { AccountChip, SignInSheet } from './Account';

/**
 * The contributor portal (/contribute) and the owner's review page (/review).
 * Consent comes first; uploads unlock only once it is on file (and, for anyone under 18,
 * once a parent or guardian has confirmed). Files are private: only the contributor and
 * the owner can open them.
 */

export const FORM_VERSION = 'v1-2026-11';

interface Consent {
  id: string;
  user_id: string;
  full_name: string;
  adult: boolean;
  guardian_name: string | null;
  guardian_email: string | null;
  sounds: boolean;
  face: boolean;
  face_hollow: boolean;
  credit: string | null;
  agreed_at: string;
  guardian_status: 'not_needed' | 'pending' | 'confirmed';
  withdrawn_at: string | null;
}
interface Contribution {
  id: string;
  user_id: string;
  kind: 'sound' | 'photo';
  path: string;
  original_name: string | null;
  bytes: number | null;
  note: string | null;
  status: 'pending' | 'approved' | 'declined' | 'withdrawn';
  owner_note: string | null;
  created_at: string;
}

const MAX_BYTES = 15 * 1024 * 1024;
const SOUND_TYPES = 'audio/*,.m4a,.mp3,.wav,.ogg,.webm,.flac,.aac';
const PHOTO_TYPES = 'image/jpeg,image/png,image/webp,image/heic,image/heif,.heic';
const STATUS: Record<Contribution['status'], string> = { pending: 'Waiting for review', approved: 'Approved', declined: 'Not used', withdrawn: 'Removed' };

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="portal">
      <header className="portal-top">
        <a href="/" className="portal-brand">
          Q<b>OO</b>BD
        </a>
        <AccountChip />
      </header>
      <main className="portal-main">
        <h1>{title}</h1>
        {children}
      </main>
      <SignInSheet />
    </div>
  );
}

/** The agreement itself, in plain language. Versioned: a change of wording means a new version. */
function ConsentText() {
  return (
    <div className="consent-text">
      <p>
        <b>What you're giving.</b> Sounds you record, a photo of your face, or both. You're volunteering: there's no payment.
      </p>
      <p>
        <b>Sounds.</b> We may trim, clean up, add echo to and change the pitch of your recordings, and play them as rare background sounds in QOOBD,
        heard by every player.
      </p>
      <p>
        <b>Your face.</b> We turn your photo into a painted-style avatar that any player can see behind a helmet visor, including strangers in online
        matches. It's named with a callsign, never your real name.
      </p>
      <p>
        <b>The mirror version (optional).</b> A deliberately creepy version of your avatar, with hollow black eyes, that flashes for a quarter of a
        second in the game's rare mirror rooms.
      </p>
      <p>
        <b>What stays private.</b> Your real name, your email and your parent or guardian's details are seen only by the game's owner. Your uploads are
        stored privately and are never posted as public links.
      </p>
      <p>
        <b>Changing your mind.</b> You can withdraw at any time on this page. We delete your uploads and take anything made from them out of the game in
        the next update. We can't recall screenshots or recordings other players may already have made.
      </p>
      <p>
        <b>Under 18?</b> A parent or guardian has to agree too before you can upload anything.
      </p>
      <p>
        <b>Your rights.</b> Your recordings and photo stay yours. You're giving QOOBD free, non-exclusive permission to use and edit them in the game and
        in things that show the game (like trailers and screenshots) for as long as you leave this consent in place.
      </p>
    </div>
  );
}

function ConsentForm({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState('');
  const [callsign, setCs] = useState(useAccount.getState().callsign ?? '');
  const [adult, setAdult] = useState<boolean | null>(null);
  const [gName, setGName] = useState('');
  const [gEmail, setGEmail] = useState('');
  const [sounds, setSounds] = useState(false);
  const [face, setFace] = useState(false);
  const [hollow, setHollow] = useState(false);
  const [credit, setCredit] = useState('');
  const [agree, setAgree] = useState(false);
  const [sign, setSign] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const signed = sign.trim().toLowerCase() === name.trim().toLowerCase() && name.trim().length >= 2;
  const ok =
    signed && agree && adult !== null && (sounds || face) && (adult || (gName.trim().length >= 2 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(gEmail.trim())));
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ok) return;
    setBusy(true);
    setErr(null);
    if (callsign.trim() && callsign.trim() !== useAccount.getState().callsign) {
      const csErr = await setCallsign(callsign);
      if (csErr) {
        setErr(csErr);
        setBusy(false);
        return;
      }
    }
    const { error } = await supabase.rpc('submit_consent', {
      p_form_version: FORM_VERSION,
      p_full_name: name,
      p_adult: adult,
      p_guardian_name: adult ? null : gName,
      p_guardian_email: adult ? null : gEmail,
      p_sounds: sounds,
      p_face: face,
      p_face_hollow: face && hollow,
      p_credit: credit,
    });
    setBusy(false);
    if (error) setErr(error.message || "Couldn't save your consent. Try again.");
    else onDone();
  };
  return (
    <form className="card portal-card" onSubmit={submit}>
      <div className="eyebrow">Step 1 · Consent</div>
      <h2>Before you send anything</h2>
      <ConsentText />
      <fieldset className="field">
        <legend className="lbl">What would you like to contribute?</legend>
        <label className="check">
          <input type="checkbox" checked={sounds} onChange={(e) => setSounds(e.target.checked)} /> Sounds
        </label>
        <label className="check">
          <input type="checkbox" checked={face} onChange={(e) => setFace(e.target.checked)} /> My face as an avatar
        </label>
        {face && (
          <label className="check sub">
            <input type="checkbox" checked={hollow} onChange={(e) => setHollow(e.target.checked)} /> Also the creepy mirror version (optional)
          </label>
        )}
      </fieldset>
      <label className="field">
        <span className="lbl">Your full name (kept private, for the consent record)</span>
        <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" maxLength={100} required />
      </label>
      <label className="field">
        <span className="lbl">Callsign (shown in the game instead of your name)</span>
        <input value={callsign} onChange={(e) => setCs(e.target.value)} maxLength={20} placeholder="e.g. Nightjar" />
      </label>
      <label className="field">
        <span className="lbl">Credit me as (optional, a first name or handle)</span>
        <input value={credit} onChange={(e) => setCredit(e.target.value)} maxLength={40} />
      </label>
      <fieldset className="field">
        <legend className="lbl">Are you 18 or older?</legend>
        <div className="row">
          <label className="check">
            <input type="radio" name="adult" checked={adult === true} onChange={() => setAdult(true)} /> Yes
          </label>
          <label className="check">
            <input type="radio" name="adult" checked={adult === false} onChange={() => setAdult(false)} /> No
          </label>
        </div>
      </fieldset>
      {adult === false && (
        <>
          <p className="note">We'll need your parent or guardian to agree to this too. Your uploads unlock once they have.</p>
          <label className="field">
            <span className="lbl">Parent or guardian's name</span>
            <input value={gName} onChange={(e) => setGName(e.target.value)} maxLength={100} required />
          </label>
          <label className="field">
            <span className="lbl">Parent or guardian's email</span>
            <input type="email" value={gEmail} onChange={(e) => setGEmail(e.target.value)} inputMode="email" required />
          </label>
        </>
      )}
      <label className="check">
        <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} /> I've read this and I agree.
      </label>
      <label className="field">
        <span className="lbl">Type your full name again to sign</span>
        <input value={sign} onChange={(e) => setSign(e.target.value)} maxLength={100} />
      </label>
      {err && (
        <p className="note err" role="alert">
          {err}
        </p>
      )}
      <div className="row">
        <button className="cta" type="submit" disabled={!ok || busy}>
          {busy ? 'Saving…' : 'Sign and continue'}
        </button>
      </div>
      <p className="note">Form {FORM_VERSION}</p>
    </form>
  );
}

function Uploader({ kind, consentId, disabled, onDone }: { kind: 'sound' | 'photo'; consentId: string; disabled: boolean; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const uid = useAccount((s) => s.session?.user.id);
  const send = async (files: FileList | null) => {
    if (!files || !uid) return;
    setBusy(true);
    setMsg(null);
    let sent = 0;
    for (const f of Array.from(files).slice(0, kind === 'photo' ? 1 : 10)) {
      if (f.size > MAX_BYTES) {
        setMsg(`${f.name} is over 15 MB.`);
        continue;
      }
      const safe = f.name.replace(/[^\w.-]+/g, '_').slice(-60);
      const path = `${uid}/${kind === 'sound' ? 'sounds' : 'photo'}/${Date.now()}-${safe}`;
      const up = await supabase.storage.from('contributions').upload(path, f, { contentType: f.type || undefined, upsert: false });
      if (up.error) {
        setMsg(`Couldn't upload ${f.name}. ${/mime|type/i.test(up.error.message) ? 'That file type isn’t accepted.' : 'Try again.'}`);
        continue;
      }
      const rec = await supabase.rpc('record_contribution', { p_kind: kind, p_path: path, p_original_name: f.name, p_bytes: f.size, p_note: note });
      if (rec.error) {
        await supabase.storage.from('contributions').remove([path]);
        setMsg(rec.error.message);
        continue;
      }
      sent++;
    }
    setBusy(false);
    if (sent) {
      setNote('');
      setMsg(`Sent ${sent} ${kind === 'sound' ? (sent === 1 ? 'sound' : 'sounds') : 'photo'}. Thank you!`);
      onDone();
    }
  };
  return (
    <div className="uploader" data-consent={consentId}>
      <label className="field">
        <span className="lbl">{kind === 'sound' ? 'A note about these sounds (optional)' : 'A note about your photo (optional)'}</span>
        <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder={kind === 'sound' ? 'e.g. whisper, slowed down' : ''} />
      </label>
      <label className={`cta upload-btn${disabled || busy ? ' off' : ''}`}>
        {busy ? 'Uploading…' : kind === 'sound' ? 'Choose sound files' : 'Choose a photo'}
        <input
          type="file"
          accept={kind === 'sound' ? SOUND_TYPES : PHOTO_TYPES}
          multiple={kind === 'sound'}
          disabled={disabled || busy}
          onChange={(e) => {
            void send(e.target.files);
            e.target.value = '';
          }}
        />
      </label>
      {msg && (
        <p className="note" role="status">
          {msg}
        </p>
      )}
    </div>
  );
}

export function ContributePage() {
  const ready = useAccount((s) => s.ready);
  const session = useAccount((s) => s.session);
  const [consent, setConsent] = useState<Consent | null | undefined>(undefined);
  const [items, setItems] = useState<Contribution[]>([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    if (!session) return;
    const [c, k] = await Promise.all([
      supabase.from('consents').select('*').eq('user_id', session.user.id).is('withdrawn_at', null).maybeSingle(),
      supabase.from('contributions').select('*').eq('user_id', session.user.id).neq('status', 'withdrawn').order('created_at', { ascending: false }),
    ]);
    setConsent((c.data as Consent | null) ?? null);
    setItems((k.data as Contribution[] | null) ?? []);
  }, [session]);
  useEffect(() => {
    void load();
  }, [load]);

  if (!ready) return <Shell title="Contribute">{null}</Shell>;
  if (!session)
    return (
      <Shell title="Contribute">
        <div className="card portal-card">
          <p>
            Record creepy sounds for the cube, or become one of the faces behind the visors. You'll need an account first, so we can keep your consent on
            file and let you change your mind later.
          </p>
          <div className="row">
            <button className="cta" type="button" onClick={() => useAccount.setState({ signInOpen: true })}>
              Sign in to contribute
            </button>
          </div>
        </div>
      </Shell>
    );
  if (consent === undefined) return <Shell title="Contribute">{null}</Shell>;
  if (consent === null)
    return (
      <Shell title="Contribute">
        <ConsentForm onDone={() => void load()} />
      </Shell>
    );

  const unlocked = consent.guardian_status !== 'pending';
  const remove = async (c: Contribution) => {
    setBusy(true);
    await supabase.storage.from('contributions').remove([c.path]);
    await supabase.rpc('remove_contribution', { p_id: c.id });
    setBusy(false);
    void load();
  };
  const withdraw = async () => {
    if (!window.confirm('Withdraw your consent? This deletes everything you have uploaded.')) return;
    setBusy(true);
    const paths = items.map((i) => i.path);
    if (paths.length) await supabase.storage.from('contributions').remove(paths);
    await supabase.rpc('withdraw_consent');
    setBusy(false);
    void load();
  };
  const photoIn = items.some((i) => i.kind === 'photo' && (i.status === 'pending' || i.status === 'approved'));
  return (
    <Shell title="Contribute">
      <div className="card portal-card">
        <div className="eyebrow">Consent on file</div>
        <p>
          Signed {new Date(consent.agreed_at).toLocaleDateString()} for {[consent.sounds && 'sounds', consent.face && 'your face', consent.face_hollow && 'the mirror version'].filter(Boolean).join(', ')}.
        </p>
        {!unlocked && (
          <p className="note warn">
            Waiting for your parent or guardian ({consent.guardian_name}). Uploads unlock as soon as they've agreed; the game's owner will be in touch
            with them.
          </p>
        )}
      </div>

      {consent.sounds && (
        <div className="card portal-card">
          <div className="eyebrow">Step 2 · Sounds</div>
          <p className="note">
            A phone's voice-memo app is perfect. One sound per file, in a quiet room, with a second of silence before and after. Whispers, breaths,
            knocks, scrapes, footsteps, hums. No names, places or anything that identifies you. Up to 15 MB each.
          </p>
          <Uploader kind="sound" consentId={consent.id} disabled={!unlocked || busy} onDone={() => void load()} />
        </div>
      )}
      {consent.face && (
        <div className="card portal-card">
          <div className="eyebrow">Step 2 · Your face</div>
          <p className="note">One photo looking straight at the camera, head and shoulders, in even light, without glasses glare. A neutral face or a slight smile works best.</p>
          <Uploader kind="photo" consentId={consent.id} disabled={!unlocked || busy || photoIn} onDone={() => void load()} />
          {photoIn && <p className="note">You have a photo in. Remove it below to send a different one.</p>}
        </div>
      )}

      {items.length > 0 && (
        <div className="card portal-card">
          <div className="eyebrow">What you've sent</div>
          <ul className="contrib-list">
            {items.map((i) => (
              <li key={i.id}>
                <span>
                  {i.kind === 'sound' ? '♪' : '◉'} {i.original_name ?? 'file'}
                </span>
                <span className={`pill ${i.status}`}>{STATUS[i.status]}</span>
                {i.status === 'pending' && (
                  <button type="button" className="linkish" disabled={busy} onClick={() => void remove(i)}>
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="card portal-card">
        <div className="eyebrow">Changed your mind?</div>
        <p className="note">Withdrawing deletes your uploads and takes anything made from them out of the game in the next update.</p>
        <div className="row">
          <button className="cta alt" type="button" disabled={busy} onClick={() => void withdraw()}>
            Withdraw my consent
          </button>
        </div>
      </div>
    </Shell>
  );
}

/** The owner's review page: every consent and contribution, with approve and decline. */
export function ReviewPage() {
  const ready = useAccount((s) => s.ready);
  const role = useAccount((s) => s.role);
  const session = useAccount((s) => s.session);
  const [consents, setConsents] = useState<Consent[]>([]);
  const [items, setItems] = useState<Contribution[]>([]);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const load = useCallback(async () => {
    const [c, k] = await Promise.all([
      supabase.from('consents').select('*').order('agreed_at', { ascending: false }),
      supabase.from('contributions').select('*').neq('status', 'withdrawn').order('created_at', { ascending: false }),
    ]);
    setConsents((c.data as Consent[] | null) ?? []);
    setItems((k.data as Contribution[] | null) ?? []);
  }, []);
  useEffect(() => {
    if (role === 'owner') void load();
  }, [role, load]);
  if (!ready) return <Shell title="Review">{null}</Shell>;
  if (!session || role !== 'owner')
    return (
      <Shell title="Review">
        <div className="card portal-card">
          <p>This page is for the game's owner.</p>
        </div>
      </Shell>
    );
  const open = async (c: Contribution) => {
    const { data } = await supabase.storage.from('contributions').createSignedUrl(c.path, 600);
    if (data?.signedUrl) setUrls((u) => ({ ...u, [c.id]: data.signedUrl }));
  };
  const review = async (c: Contribution, status: 'approved' | 'declined') => {
    await supabase.rpc('owner_review', { p_id: c.id, p_status: status, p_note: null });
    void load();
  };
  const confirm = async (c: Consent) => {
    if (!window.confirm(`Confirm you've spoken with ${c.guardian_name} and they agree for ${c.full_name}?`)) return;
    await supabase.rpc('owner_confirm_guardian', { p_consent: c.id });
    void load();
  };
  const byUser = (uid: string) => consents.find((c) => c.user_id === uid && !c.withdrawn_at);
  return (
    <Shell title="Review contributions">
      <div className="card portal-card">
        <div className="eyebrow">Consents</div>
        {consents.length === 0 && <p className="note">None yet.</p>}
        <ul className="contrib-list">
          {consents.map((c) => (
            <li key={c.id}>
              <span>
                <b>{c.full_name}</b> {c.adult ? '(18+)' : `(under 18 · ${c.guardian_name}, ${c.guardian_email})`} ·{' '}
                {[c.sounds && 'sounds', c.face && 'face', c.face_hollow && 'mirror'].filter(Boolean).join(', ')}
                {c.credit ? ` · credit "${c.credit}"` : ''}
              </span>
              <span className={`pill ${c.withdrawn_at ? 'withdrawn' : c.guardian_status === 'pending' ? 'pending' : 'approved'}`}>
                {c.withdrawn_at ? 'Withdrawn' : c.guardian_status === 'pending' ? 'Guardian pending' : 'Active'}
              </span>
              {!c.withdrawn_at && c.guardian_status === 'pending' && (
                <button type="button" className="linkish" onClick={() => void confirm(c)}>
                  Guardian agreed
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>
      <div className="card portal-card">
        <div className="eyebrow">Uploads</div>
        {items.length === 0 && <p className="note">None yet.</p>}
        <ul className="contrib-list review">
          {items.map((c) => (
            <li key={c.id}>
              <span>
                {c.kind === 'sound' ? '♪' : '◉'} {c.original_name} · {byUser(c.user_id)?.full_name ?? 'unknown'}
                {c.note ? ` · "${c.note}"` : ''}
              </span>
              <span className={`pill ${c.status}`}>{STATUS[c.status]}</span>
              {urls[c.id] ? (
                c.kind === 'sound' ? (
                  <audio controls src={urls[c.id]} preload="none" />
                ) : (
                  <img src={urls[c.id]} alt="" className="review-photo" />
                )
              ) : (
                <button type="button" className="linkish" onClick={() => void open(c)}>
                  {c.kind === 'sound' ? 'Listen' : 'View'}
                </button>
              )}
              <span className="row">
                <button type="button" className="linkish" onClick={() => void review(c, 'approved')}>
                  Approve
                </button>
                <button type="button" className="linkish" onClick={() => void review(c, 'declined')}>
                  Decline
                </button>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </Shell>
  );
}
